import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import visuallyHidden from '@mui/utils/visuallyHidden';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { makeStyles } from '../../../tools/makeStyles';
import { defaultDescription, defaultTitle } from '../constants/constants';
import Header from '../common/Header.jsx';
import Footer from '../common/Footer.jsx';
import { setTabIndex, buildRatingOptionArialLabel } from '../../helpers/accessibility_helper';
import { useNumberKeySelectorCallback } from '../../helpers/number_key_selector_helper';
import { useKeySelect } from '../../helpers/key_select_helper';
import { DEFAULT_TRANSLATION } from '../../helpers/constants';

// NOTE: This percentage is the maximum fraction that will fit as a
// result of 100% / `maxRating`.
// If `maxRating` is 10: 100 / 10 = 10
// If `maxRating` is 11: 100 / 11 = 9.09
const WIDTH_PERCENT = 9.09;

const useStyles = makeStyles((theme) => ({
  screenReaderDescription: {
    display: 'block',
  },
  scaleRoot: {
    boxSizing: 'border-box',
    display: 'block',
    marginTop: 24,
  },
  scaleContainer: {
    boxSizing: 'border-box',
    display: 'inline-block',
    width: '100%',
    color: 'rgb(79, 176, 174)',
    backgroundColor: 'rgba(79, 176, 174, 0.1)',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: 'rgb(79, 176, 174)',
    borderImage: 'initial',
    borderRadius: 3,
  },
  scaleButton: {
    display: 'inline-block',
    width: `${WIDTH_PERCENT}%`,
    minWidth: 'unset',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    userSelect: 'none',
    borderRight: '#0F7B85 1px solid',
    borderRadius: 0,
    color: theme.palette.text.primary,
    '&:hover, &:focus': {
      backgroundColor: '#0F7B85',
      color: 'white',
    },
    '&:last-child': {
      borderRightColor: 'transparent',
    },
  },
  scaleButtonContent: {
    lineHeight: '4em',
    whiteSpace: 'nowrap',
  },
  selectedScaleButton: {
    backgroundColor: '#0F7B85',
    color: 'white',
  },
  labelContainer: {
    width: 'auto',
    marginTop: 10,
    justifyContent: 'space-between',
    color: '#4f4f4f',
  },
  maxLabel: {
    textAlign: 'right',
  },
}));

const ENPSPreview = ({
  maxLabel = 'Strongly agree',
  minLabel = 'Strongly disagree',
  showTextComment = false,
  showSkipButton = false,
  savedAnswer,
  handleAnswer = () => {},
  onCommentChange = () => {},
  questionId = -1,
  isRequired = false,
  showDescription = false,
  title = defaultTitle,
  titleRef,
  titleAriaLabel,
  commentAriaLabel,
  description = defaultDescription,
  allowKeyboardSelection = true,
  isActive = true,
  commentsLabel,
  commentsPlaceholder,
  minRating = 0,
  translation = DEFAULT_TRANSLATION,
}) => {
  const styles = useStyles();

  const emptyAnswer = { value: null, questionId, comment: '' };
  const [answer, setAnswer] = useState(savedAnswer || emptyAnswer);
  const [, selectAnswer] = useState(false);

  const handleOptionalComment = (event) => {
    const newAnswer = { ...answer, comment: event.target.value };
    setAnswer(newAnswer);
    onCommentChange(event.target.value);
  };

  const numberKeySelector = useNumberKeySelectorCallback(0, 10, (key) => {
    handleAnswerSelection(key);
  });

  useKeySelect(allowKeyboardSelection, numberKeySelector);

  const handleAnswerSelection = (index) => {
    selectAnswer(true);
    const newAnswer = { ...answer, value: index };
    setAnswer(newAnswer);
    handleAnswer(newAnswer, !showTextComment);
  };

  const onConfirm = (hasAnswer = false) => {
    if (hasAnswer) {
      handleAnswer(answer);
    } else {
      setAnswer(emptyAnswer);
      selectAnswer(true);
      handleAnswer(emptyAnswer);
    }
  };

  const enpsOptions = [];
  for (let i = minRating; i <= 10; i++) {
    enpsOptions.push(i);
  }

  const isSelectedAnswer = (index) => answer && answer.value === index;

  return (
    <div>
      <Header
        isRequired={isRequired}
        questionId={questionId}
        title={title}
        titleRef={titleRef}
        titleAriaLabel={titleAriaLabel}
        description={description}
        showDescription={showDescription}
        isActive={isActive}
        translation={translation}
      />

      <Box className={classNames(styles.screenReaderDescription)} sx={visuallyHidden}>
        {`0 is ${minLabel}, 10 is ${maxLabel}`}
      </Box>

      <div className={styles.scaleRoot}>
        <div className={styles.scaleContainer}>
          {enpsOptions.map((item, index) => {
            const ariaLabel = buildRatingOptionArialLabel(translation, enpsOptions, index, index, minLabel, maxLabel);
            return (
              <Button
                className={classNames(
                  {
                    [styles.selectedScaleButton]: isSelectedAnswer(index),
                  },
                  styles.scaleButton,
                )}
                key={index}
                onClick={() => handleAnswerSelection(index)}
                tabIndex={setTabIndex(isActive)}
                aria-hidden={!isActive}
                aria-label={ariaLabel}
                aria-pressed={index === answer?.value}
              >
                <Typography className={styles.scaleButtonContent} aria-hidden>
                  {index}
                </Typography>
              </Button>
            );
          })}
        </div>

        <Grid
          container
          direction="row"
          className={styles.labelContainer}
          style={{
            width: `${WIDTH_PERCENT * enpsOptions.length}%`,
          }}
        >
          <Grid item>
            {minLabel && (
              <Typography className={styles.minLabel} xs={6} aria-hidden>
                {minLabel}
              </Typography>
            )}
          </Grid>
          <Grid item>
            {maxLabel && (
              <Typography className={styles.maxLabel} xs={6} aria-hidden>
                {maxLabel}
              </Typography>
            )}
          </Grid>
        </Grid>
      </div>

      <Footer
        value={answer.value}
        showSkipButton={showSkipButton && answer.value === null}
        showOkButton={showTextComment && answer.value !== null}
        onConfirm={onConfirm}
        showTextComment={showTextComment}
        commentAriaLabel={commentAriaLabel}
        optionalComment={answer.comment}
        onChangeComment={handleOptionalComment}
        isActive={isActive}
        label={commentsLabel}
        placeholder={commentsPlaceholder}
        translation={translation}
      />
    </div>
  );
};

ENPSPreview.propTypes = {
  questionId: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  savedAnswer: PropTypes.object,
  handleAnswer: PropTypes.func,
  onCommentChange: PropTypes.func,
  maxLabel: PropTypes.string,
  minLabel: PropTypes.string,
  showSkipButton: PropTypes.bool,
  showTextComment: PropTypes.bool,
  showDescription: PropTypes.bool,
  allowKeyboardSelection: PropTypes.bool,
  isRequired: PropTypes.bool,
  isActive: PropTypes.bool,
  minRating: PropTypes.number,
};

export default ENPSPreview;
